import React, { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import API_URL from "../config";
import { useIdioma } from "../context/IdiomaContext";
import { useEstilo } from "../context/EstiloContext";
import { obtenerEstilo } from "../temas/catalogoEstilos";
import { colorTextoLegible } from "../utils/contraste";
import CargandoSpinner from "./CargandoSpinner";

const getFotoProfesor = (foto) => {
  if (!foto) return `${API_URL}/Fotos_profesores/default.png`;
  if (foto.startsWith("http")) return foto;
  return `${API_URL}/Fotos_profesores/${foto}`;
};

const cardVariants = {
  hidden: { opacity: 0, y: 25 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.06, duration: 0.4 },
  }),
};

/* ⭐ FUNCIÓN NUEVA QUE RESPETA CONFIGURACIÓN */
const contarHorariosFuturos = (horarios, config) => {
  const ahora = new Date();

  const tiempoConfigMs =
    (config.dias * 24 * 60 * 60 * 1000) +
    (config.horas * 60 * 60 * 1000) +
    (config.minutos * 60 * 1000);

  return horarios.filter((h) => {
    const fecha = new Date(h.fecha);

    const [hi, mi, si] = h.hora_inicio.split(":").map(Number);
    const [hf, mf, sf] = h.hora_fin.split(":").map(Number);

    const inicio = new Date(fecha);
    inicio.setHours(hi, mi, si || 0);

    const fin = new Date(fecha);
    fin.setHours(hf, mf, sf || 0);

    if (!(fin > ahora && h.disponible === 1)) return false;

    const fechaLimiteVisible = new Date(inicio.getTime() - tiempoConfigMs);

    if (!(ahora < fechaLimiteVisible)) return false;

    // 🔥 Tope de días hacia el futuro — solo aplica si el servicio ya
    // tiene una configuración nueva asignada (config.origen === "servicio").
    // Mientras no la tenga, se mantiene el comportamiento de siempre (sin tope).
    if (config.origen === "servicio" && config.diasVisibles > 0) {
      const limiteFuturo = new Date();
      limiteFuturo.setDate(limiteFuturo.getDate() + config.diasVisibles);
      if (inicio > limiteFuturo) return false;
    }

    return true;
  }).length;
};

const ProfesoresList = ({ usuario, servicioActual, fondoEfectivo, onSeleccionar = () => {} }) => {
  const { t } = useIdioma();
  const estiloGlobal = useEstilo();
  const estilo = servicioActual?.estilo_visual ? obtenerEstilo(servicioActual.estilo_visual) : estiloGlobal;
  const [profesores, setProfesores] = useState([]);
  const [etiquetasServicio, setEtiquetasServicio] = useState([]);
  const [filtro, setFiltro] = useState("");
  const [loading, setLoading] = useState(true);
  const colorAcento = servicioActual?.color || "#2563eb";
  const colorTexto = colorTextoLegible(fondoEfectivo);

  useEffect(() => {
    const fetchProfesores = async () => {
      try {
        // 🔹 Traer configuración (la del servicio si tiene, si no, la de siempre)
        const configRes = await axios.get(
          `${API_URL}/api/configuraciones-horarios/efectiva`,
          { params: { servicio_id: servicioActual?.id } }
        );

        // 🔹 Traer qué etiquetas hacen que un profesor pertenezca a ESTE
        // servicio (antes esto era fijo: solo etiqueta.id === 1 o 2).
        // Se define en "Gestionar Etiquetas Docentes" → "Etiquetas por servicio".
        const etiquetasRes = servicioActual?.id
          ? await axios.get(
              `${API_URL}/api/etiquetas-docentes/servicio/${servicioActual.id}`
            )
          : { data: [] };
        setEtiquetasServicio(etiquetasRes.data || []);
        const config = {
          dias: Number(configRes.data?.dias_ocultar_antes) || 0,
          horas: Number(configRes.data?.horas_ocultar_antes) || 0,
          minutos: Number(configRes.data?.minutos_ocultar_antes) || 0,
          diasVisibles: Number(configRes.data?.dias_visibles_futuro) || 0,
          origen: configRes.data?.origen,
        };

        const res = await axios.get(`${API_URL}/api/profesores`);
        const profesoresData = res.data;

        const profesoresConDatos = await Promise.all(
          profesoresData.map(async (prof) => {
            try {
              const etiquetaRes = await axios.get(
                `${API_URL}/api/etiquetas-docentes/profesor/${prof.id}`
              );

              const horariosRes = await axios.get(
                `${API_URL}/api/horarios/profesor/${prof.id}`
              );

              const cantidadHorarios = contarHorariosFuturos(
                horariosRes.data || [],
                config
              );

              return {
                ...prof,
                etiqueta: etiquetaRes.data,
                tieneHorarios: cantidadHorarios > 0,
                cantidadHorarios,
              };
            } catch {
              return {
                ...prof,
                etiqueta: null,
                tieneHorarios: false,
                cantidadHorarios: 0,
              };
            }
          })
        );

        setProfesores(profesoresConDatos);
      } catch (err) {
        console.error("Error al cargar profesores:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchProfesores();
  }, [servicioActual]);

  // 🔵 ID de las etiquetas que pertenecen a este servicio (dinámico, ya no fijo)
  const idsEtiquetasValidas = etiquetasServicio.map((e) => e.id);

  const profesoresFiltrados = profesores.filter(
    (p) =>
      p.nombre.toLowerCase().includes(filtro.toLowerCase()) &&
      p.etiqueta &&
      idsEtiquetasValidas.includes(p.etiqueta.id)
  );

  // 🔵 Agrupa dinámicamente por etiqueta (una sección por cada etiqueta
  // ligada a este servicio, en vez de los títulos fijos "Primaria"/"Secundaria")
  const gruposPorEtiqueta = etiquetasServicio.map((etiqueta) => ({
    etiqueta,
    profesores: profesoresFiltrados
      .filter((p) => p.etiqueta.id === etiqueta.id)
      .sort((a, b) => b.tieneHorarios - a.tieneHorarios),
  }));

  // Botón "Ver horarios" — respeta el estilo elegido (sólido u outline)
  const estiloBoton = (disponible) => {
    const colorEstado = disponible ? "#16a34a" : "#ef4444";
    if (estilo.botonEstilo === "outline") {
      return {
        background: "white",
        color: colorEstado,
        border: `2px solid ${colorEstado}`,
      };
    }
    return { background: colorEstado, color: "white", border: "none" };
  };

  const renderProfesores = (lista) =>
    lista.map((prof, i) =>
      estilo.layoutDocentes === "lista" ? (
        // ===== ESTILO "LISTA COMPACTA" — fila con foto pequeña + nombre + botón =====
        <motion.div
          key={prof.id}
          custom={i}
          initial="hidden"
          animate="visible"
          variants={cardVariants}
          style={{
            background: "white",
            borderRadius: estilo.cardRadius,
            boxShadow: estilo.cardShadow,
            border: estilo.cardBorder,
            borderLeft: `4px solid ${prof.tieneHorarios ? "#22c55e" : "#ef4444"}`,
            padding: "12px 16px",
            display: "flex",
            alignItems: "center",
            gap: 14,
            opacity: prof.tieneHorarios ? 1 : 0.85,
          }}
        >
          <img
            src={getFotoProfesor(prof.foto)}
            alt={prof.nombre}
            onError={(e) => (e.currentTarget.src = `${API_URL}/Fotos_profesores/default.png`)}
            style={{
              width: estilo.fotoTamano,
              height: estilo.fotoTamano,
              borderRadius: "50%",
              border: `3px solid ${colorAcento}`,
              objectFit: "cover",
              flexShrink: 0,
            }}
          />

          <div style={{ flex: 1, minWidth: 0 }}>
            <h4 style={{ fontSize: 15, fontWeight: 700, color: "#0f172a", margin: 0 }}>{prof.nombre}</h4>
            <p style={{ fontSize: 12, color: "#64748b", margin: "2px 0" }}>{prof.email}</p>
            <p
              style={{
                fontSize: 12,
                fontWeight: 600,
                color: prof.tieneHorarios ? "#16a34a" : "#ef4444",
                margin: 0,
              }}
            >
              {prof.cantidadHorarios} {t("lista_horarios_disponibles")}
            </p>
          </div>

          <button
            disabled={!prof.tieneHorarios}
            onClick={() => prof.tieneHorarios && onSeleccionar(prof)}
            style={{
              ...estiloBoton(prof.tieneHorarios),
              borderRadius: estilo.botonRadius,
              padding: "10px 18px",
              fontSize: 13,
              fontWeight: 600,
              cursor: prof.tieneHorarios ? "pointer" : "not-allowed",
              whiteSpace: "nowrap",
              flexShrink: 0,
            }}
          >
            {t("lista_ver_horarios")}
          </button>
        </motion.div>
      ) : (
        // ===== ESTILO "CLÁSICO" / "MINIMALISTA" — tarjeta =====
        <motion.div
          key={prof.id}
          className="profesor-card"
          custom={i}
          initial="hidden"
          animate="visible"
          variants={cardVariants}
          whileHover={{ scale: prof.tieneHorarios ? 1.03 : 1.01 }}
          style={{
            background: prof.tieneHorarios ? "white" : "#f9fafb",
            borderRadius: estilo.cardRadius,
            padding: "26px 20px",
            textAlign: "center",
            boxShadow: estilo.cardShadow,
            border: `2px solid ${prof.tieneHorarios ? "#22c55e" : "#ef4444"}`,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            minHeight: "300px",
            opacity: prof.tieneHorarios ? 1 : 0.85,
          }}
        >
          <div className="profesor-foto-wrap" style={{ position: "relative", width: estilo.fotoTamano, height: estilo.fotoTamano, marginBottom: 12 }}>
            <img
              className="profesor-foto"
              src={getFotoProfesor(prof.foto)}
              alt={prof.nombre}
              onError={(e) =>
                (e.currentTarget.src = `${API_URL}/Fotos_profesores/default.png`)
              }
              style={{
                width: estilo.fotoTamano,
                height: estilo.fotoTamano,
                borderRadius: "50%",
                border: `4px solid ${colorAcento}`,
                objectFit: "cover",
              }}
            />
            <div
              style={{
                position: "absolute",
                bottom: 6,
                right: 6,
                width: 16,
                height: 16,
                borderRadius: "50%",
                background: prof.tieneHorarios ? "#22c55e" : "#ef4444",
                border: "2px solid white",
              }}
            />
          </div>

          {prof.etiqueta && (
            <span
              style={{
                backgroundColor: prof.etiqueta.color,
                color: "white",
                padding: "5px 14px",
                borderRadius: "999px",
                fontSize: 12,
                fontWeight: 600,
                marginBottom: 8,
              }}
            >
              {prof.etiqueta.nombre}
            </span>
          )}

          <h4 style={{ fontSize: 16, fontWeight: 700, color: "#0f172a", marginBottom: 4 }}>
            {prof.nombre}
          </h4>

          <p style={{ fontSize: 13, marginBottom: 10, color: prof.tieneHorarios ? "#16a34a" : "#ef4444", fontWeight: 600 }}>
            {prof.cantidadHorarios} {t("lista_horarios_disponibles")}
          </p>

          <p style={{ fontSize: 13, color: "#64748b", marginBottom: 14 }}>
            {prof.email}
          </p>

          <button
            disabled={!prof.tieneHorarios}
            onClick={() => prof.tieneHorarios && onSeleccionar(prof)}
            style={{
              ...estiloBoton(prof.tieneHorarios),
              borderRadius: estilo.botonRadius,
              padding: "10px 16px",
              fontSize: 13,
              fontWeight: 600,
              cursor: prof.tieneHorarios ? "pointer" : "not-allowed",
              width: "100%",
            }}
          >
            {t("lista_ver_horarios")}
          </button>
        </motion.div>
      )
    );

  const estaBuscando = filtro.trim() !== "";

  const contenedorProps =
    estilo.layoutDocentes === "lista"
      ? { className: "", style: { display: "flex", flexDirection: "column", gap: 12, maxWidth: 720, marginInline: "auto" } }
      : { className: "profesores-grid", style: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(240px,1fr))", gap: 20 } };

  return (
    <div style={{ paddingTop: 25, paddingLeft: 20, paddingRight: 20, maxWidth: 1200, margin: "auto" }}>
      
      {/* 🔵 HEADER ORIGINAL */}
      <div style={{ textAlign: "center", marginBottom: 18 }}>
        <h2 style={{ color: colorTexto, fontWeight: 600, fontSize: 20, marginBottom: 5 }}>
          {t("lista_titulo")}
        </h2>
        <div style={{ width: 120, height: 3, background: colorAcento, margin: "0 auto", borderRadius: 4, opacity: 0.7 }} />
      </div>

      {/* 🔵 BUSCADOR ORIGINAL */}
      <div style={{ marginBottom: 25, textAlign: "center" }}>
        <input
          type="text"
          placeholder={t("lista_buscar")}
          value={filtro}
          onChange={(e) => setFiltro(e.target.value)}
          style={{
            padding: "10px 14px",
            borderRadius: 10,
            border: "none",
            width: "min(300px, 90%)",
            fontSize: 14,
            outline: "none",
            background: "white",
            boxShadow: "0 4px 10px rgba(0,0,0,0.1)",
          }}
        />
      </div>

      {loading && <CargandoSpinner texto={t("lista_cargando")} color={colorTexto} />}

      {estaBuscando ? (
        <div className={contenedorProps.className} style={contenedorProps.style}>
          {!loading && renderProfesores(profesoresFiltrados)}
        </div>
      ) : (
        // 🔵 Una sección por cada etiqueta ligada a este servicio (el título
        // de la sección es el nombre de la etiqueta — se controla desde
        // "Gestionar Etiquetas Docentes", ya no está fijo en el código)
        gruposPorEtiqueta.map(({ etiqueta, profesores: profesoresGrupo }) => (
          <div key={etiqueta.id}>
            <div style={{ display: "flex", alignItems: "center", gap: 15, marginBottom: 18, marginTop: 20 }}>
              <div style={{ flex: 1, height: 2, background: colorTexto, opacity: 0.4 }} />
              <h3 style={{ color: colorTexto, fontWeight: 600 }}>{t("lista_docentes_de")} {etiqueta.nombre}</h3>
              <div style={{ flex: 1, height: 2, background: colorTexto, opacity: 0.4 }} />
            </div>

            <div className={contenedorProps.className} style={contenedorProps.style}>
              {!loading && renderProfesores(profesoresGrupo)}
            </div>
          </div>
        ))
      )}

      <style>
        {`
          @media (max-width: 480px) {
            .profesores-grid {
              grid-template-columns: repeat(auto-fill, minmax(150px, 1fr)) !important;
              gap: 12px !important;
            }
            .profesor-card {
              padding: 14px 10px !important;
              min-height: unset !important;
            }
            .profesor-foto-wrap {
              width: 70px !important;
              height: 70px !important;
            }
            .profesor-foto {
              width: 70px !important;
              height: 70px !important;
            }
          }
        `}
      </style>
    </div>
  );
};

export default ProfesoresList;